import React, { useCallback, useEffect, useState } from 'react';
import { validatePhoneNumber } from 'utils/validatePhoneNumber';
import { RequestedPersonalDetailsType } from 'types/ProductDetailsType';
import { showToast } from 'components/atomic/Toast/Toast';
import { useGlobal } from 'context/global/GlobalContext';
import { getCustomData } from 'utils/customData';
import { useTranslation } from 'react-i18next';
import { useAPI } from 'utils/api';
import Input from 'components/atomic/Input';
import Button from 'components/atomic/Button';
import PhoneInput from 'components/atomic/PhoneInput';
import ConsentCheck from 'components/atomic/ConsentCheck';
import { normalizePhoneNumber } from 'utils/normalizePhoneNumber';

interface UserUpdatePayload {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  slug?: string | null;
  brandId?: string;
  customData?: {
    key: string;
    value: string;
  }[];
  smsConsent?: {
    consentType: string;
    allowed: boolean;
    consentFor: string;
    language: string;
    defaultLanguage: string;
  };
}

interface PersonalDetailsProps {
  requestedPersonalDetails?: RequestedPersonalDetailsType;
  onPersonalDetailsUpdate?: () => void;
  saveToShopify?: boolean;
  isNormalLogin?: boolean;
}

const PersonalDetails: React.FC<PersonalDetailsProps> = ({
  onPersonalDetailsUpdate = () => {},
  requestedPersonalDetails,
  saveToShopify,
  isNormalLogin,
}) => {
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [smsConsentError, setSmsConsentError] = useState<boolean>(false);
  const { t } = useTranslation('translation', { keyPrefix: 'personalDetails' });
  const {
    slug,
    smsConsent,
    productDetails,
    personalDetails,
    toggleSmsConsent,
    getPersonalDetails,
  } = useGlobal();

  const onSuccess = useCallback(() => {
    getPersonalDetails();
    onPersonalDetailsUpdate();
  }, [onPersonalDetailsUpdate, getPersonalDetails]);

  useEffect(() => {
    if (personalDetails) {
      setFirstName(personalDetails?.profile?.firstName || '');
      setLastName(personalDetails?.profile?.lastName || '');
      setPhoneNumber(personalDetails?.profile?.phoneNumber || '');
    }
  }, [personalDetails]);

  // SMS consent is never pre-checked: the shopper has to tick the box itself,
  // regardless of what the registration module was configured with. smsConsent
  // lives on GlobalProvider for the whole session, so clear anything left over
  // from an earlier form before this one renders.
  useEffect(() => {
    toggleSmsConsent(false);
  }, [toggleSmsConsent]);

  const [updateUser, loading] = useAPI<UserUpdatePayload>({
    method: 'PUT',
    endpoint: 'auth/update',
    onSuccess,
  });

  const validateUserInformation = useCallback(
    (details: UserUpdatePayload) => {
      if (
        (isNormalLogin && !details?.firstName) ||
        (requestedPersonalDetails?.name && !details?.firstName)
      ) {
        showToast({ message: t('invalidFirstName'), type: 'error' });
        return;
      }
      if (
        (isNormalLogin && !details?.lastName) ||
        (requestedPersonalDetails?.name && !details?.lastName)
      ) {
        showToast({ message: t('invalidLastName'), type: 'error' });
        return;
      }
      if (
        (isNormalLogin &&
          !details.phoneNumber &&
          !validatePhoneNumber(details.phoneNumber ?? '')) ||
        (requestedPersonalDetails?.phoneNumber.enabled &&
          details?.phoneNumber === '' &&
          !validatePhoneNumber(details.phoneNumber ?? ''))
      ) {
        showToast({
          message: t('invalidPhoneNumber'),
          type: 'error',
        });
        return;
      }
      if (
        requestedPersonalDetails?.phoneNumber.smsMarketingConsent.enabled &&
        requestedPersonalDetails.phoneNumber.smsMarketingConsent
          .requireConsent &&
        !smsConsent
      ) {
        setSmsConsentError(true);
        showToast({
          message: t('smsConsentRequired'),
          type: 'error',
        });
        return;
      }

      const customDataObject = getCustomData();
      const hasCustomData = Object.keys(customDataObject).length > 0;
      const customData = hasCustomData
        ? Object.entries(customDataObject).map(([key, value]) => ({
            key,
            value,
          }))
        : undefined;

      // Omit the key rather than sending '': JSON.stringify drops undefined but
      // transmits '', and a backend without the skip guard would $set the empty
      // string straight over a good stored number. The destructure is
      // load-bearing - ...details would otherwise carry the raw masked value.
      const { phoneNumber: rawDetailsPhone, ...detailsWithoutPhone } = details;
      const normalizedPhoneNumber = normalizePhoneNumber(
        rawDetailsPhone || phoneNumber
      );

      updateUser({
        ...detailsWithoutPhone,
        ...(normalizedPhoneNumber
          ? { phoneNumber: normalizedPhoneNumber }
          : {}),
        slug: saveToShopify ? slug : null,
        brandId: productDetails?.brand?.id,
        customData: customData,
        ...(requestedPersonalDetails?.phoneNumber.enabled && {
          smsConsent: {
            consentType: requestedPersonalDetails?.phoneNumber
              ?.smsMarketingConsent?.enabled
              ? 'explicit'
              : 'implicit',
            consentFor: 'sms',
            allowed: smsConsent,
            language:
              requestedPersonalDetails?.phoneNumber?.smsMarketingConsent
                ?.consentText || '',
            defaultLanguage:
              requestedPersonalDetails?.phoneNumber?.smsMarketingConsent
                ?.consentText || '',
          },
        }),
      });
    },
    [
      t,
      slug,
      updateUser,
      smsConsent,
      phoneNumber,
      saveToShopify,
      isNormalLogin,
      productDetails,
      requestedPersonalDetails,
    ]
  );

  return (
    <div className='relative w-full h-full flex flex-1 flex-col items-center justify-start overflow-auto pb-16 px-0.5'>
      <div className='w-full flex flex-col items-center justify-center py-1'>
        <p className='w-full pb-3 pl-1 pt-2 text-base text-left font-semibold'>
          {t('completeProfile')}
        </p>
        {isNormalLogin ||
        (requestedPersonalDetails && requestedPersonalDetails?.name) ? (
          <>
            <Input
              type='text'
              styles='my-3'
              name='firstName'
              value={firstName}
              autoComplete='given-name'
              placeholder={t('firstNameInputPlaceholder')}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Input
              type='text'
              styles='mb-1.5'
              name='lastName'
              value={lastName}
              autoComplete='family-name'
              placeholder={t('lastNameInputPlaceholder')}
              onChange={(e) => setLastName(e.target.value)}
            />
          </>
        ) : null}
        {isNormalLogin ||
        (requestedPersonalDetails &&
          requestedPersonalDetails?.phoneNumber?.enabled) ? (
          <>
            <div className='z-50 relative w-full'>
              <PhoneInput
                bottomOffset={98}
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                existingPhoneNumber={personalDetails?.profile.phoneNumber}
                placeholder={t('phoneNumberInputPlaceholder')}
              />
            </div>
            {requestedPersonalDetails?.phoneNumber?.smsMarketingConsent
              ?.enabled && (
              <ConsentCheck
                styles='!mt-1.5 !mb-0.5'
                enabled={[
                  requestedPersonalDetails?.phoneNumber?.smsMarketingConsent
                    ?.enabled || false,
                ]}
                errors={[smsConsentError]}
                texts={[
                  requestedPersonalDetails?.phoneNumber?.smsMarketingConsent
                    ?.consentText || '',
                ]}
                toggleConsentChecks={[toggleSmsConsent]}
                consentChecks={[smsConsent]}
              />
            )}
          </>
        ) : null}
      </div>
      <div className='w-full flex flex-col items-center justify-center mt-1.5 gap-3'>
        <Button
          variant='dark'
          loading={loading}
          title={t('continueButton')}
          onClick={() =>
            validateUserInformation({
              firstName,
              lastName,
              phoneNumber,
            })
          }
        />
        <Button
          variant='light'
          title={t('skip')}
          onClick={onPersonalDetailsUpdate}
        />
      </div>
    </div>
  );
};

export default PersonalDetails;
